import { useCallback, useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import omit from 'lodash/omit';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import onCreateClick from '@atlassian/jira-create-issue-legacy/src/index.tsx';
import { EcosystemAPIPrefillingUFOExperience } from '@atlassian/jira-issue-create-analytics/src/services/experiences/index.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import { gicOptInStatusResources } from '@atlassian/jira-router-resources-global-issue-create-switch/src/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import type {
	TriggerPointKeyType,
	IssueTriggerPayload,
} from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/types.tsx';
import { usePrefilledDefaultValues } from './use-prefilled-default-values/index.tsx';
import {
	transformGICPayload,
	extractEventAttributes,
	filterCustomFields,
	mergeDefaultValues,
} from './utils.tsx';
import { modernIssueCreateInGenericFunctions } from './modern-gic-generic-function/index.tsx';

const NewIssueCreateTrigger = (): null => {
	const { data } = useResource(gicOptInStatusResources);
	const subscriptionHandle = useRef<() => void>(noop);
	const [, { openIssueCreateModal }] = useTriggerIssueCreateModal();
	const currentIssueCreateVisibility = data?.gicOptInStatus;

	const getDefaultValues = usePrefilledDefaultValues();

	const openIssueCreateDialogHandler = useCallback(
		// @ts-expect-error - TS7006 - Parameter '_payload' implicitly has an 'any' type.
		async (event: unknown, _payload) => {
			let payload;
			if (event instanceof CustomEvent && !_payload) {
				payload = event.detail;
			} else {
				payload = _payload;
			}
			const actualPayload = payload?.payload;
			const triggerPointKey = payload?.triggerPointKey;
			const isPrefillingAllowed = !!actualPayload?.isPrefillingAllowed;
			const triggerPointKeySuffix = actualPayload ? actualPayload.triggerPointKeySuffix : '';
			const callback = actualPayload?.callback;
			const title = actualPayload?.title;
			// EXAMPLE of payload not to be transformed
			// {
			//     shouldNotTransform: true,
			//     payload: {
			//         customFieldsToPreserveOnFormRecreation: ['issuelinks'],
			//         defaultValues: {
			//             issueLinks: {
			//                 linkedIssueKeys: ['IC-31'],
			//             },
			//         },
			//     },
			// },

			const extractedPayload = actualPayload?.target
				? extractEventAttributes(actualPayload.target)
				: actualPayload;

			let createModalPayload = null;

			if (isPrefillingAllowed) {
				EcosystemAPIPrefillingUFOExperience.start();

				const {
					defaultValues: defaultValuesFromAPI,
					issueType,
					projectId,
					requestType,
				} = await getDefaultValues({
					prefilledFieldsData: {
						...actualPayload?.prefilledFields,
					},
					callContextAPI: true,
				});

				extractedPayload.prefilledFields = {
					...extractedPayload.prefilledFields,
					pid: projectId,
					issuetype: issueType,
					requestype: requestType,
				};

				if (!isEmpty(extractedPayload)) {
					createModalPayload = transformGICPayload(extractedPayload, isPrefillingAllowed);
				}

				const mergedDefaultValues = mergeDefaultValues(
					defaultValuesFromAPI,
					createModalPayload?.defaultValues,
				);

				if (createModalPayload) createModalPayload.defaultValues = mergedDefaultValues;

				const prefilledCustomFields = filterCustomFields(extractedPayload);

				EcosystemAPIPrefillingUFOExperience.success();

				const constructedTriggerPointKey = triggerPointKeySuffix
					? `prefilled-issue-create-${triggerPointKeySuffix}`
					: 'prefilled-issue-create';

				openIssueCreateModal({
					...omit(payload, ['shouldNotTransform']),
					payload: createModalPayload,
					// Will fix the trigger point name once approach is finalised
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					triggerPointKey: constructedTriggerPointKey as TriggerPointKeyType,
					prefilledCustomFields,
					ecosystemCallback: { callbackFunction: callback },
					...(title && { title }),
				});
			} else if (currentIssueCreateVisibility !== null) {
				if (currentIssueCreateVisibility?.isEnabled) {
					if (!isEmpty(extractedPayload)) {
						if (payload?.shouldNotTransform === true) {
							createModalPayload = extractedPayload;
						} else {
							createModalPayload = transformGICPayload(extractedPayload);
						}
					}

					openIssueCreateModal({
						...omit(payload, ['shouldNotTransform']),
						payload: createModalPayload,
						triggerPointKey,
					});
				} else {
					onCreateClick(omit(payload, ['shouldNotTransform']));
				}
			} else {
				log.safeErrorWithoutCustomerData(
					'new-issue-create-trigger',
					'visibility data is still getting downloaded',
				);
			}
		},
		[currentIssueCreateVisibility, getDefaultValues, openIssueCreateModal],
	);

	useEffect(() => {
		if (currentIssueCreateVisibility !== null) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.IssueCreate = window.IssueCreate || {};

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.IssueCreate.Visibility = currentIssueCreateVisibility; // For monolith to consume the same

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.JIRA = window.JIRA || {};

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.JIRA.Forms = {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				...window.JIRA.Forms,
				modernIssueCreate: modernIssueCreateInGenericFunctions,
			};
		}
	}, [currentIssueCreateVisibility]);

	useEffect(() => {
		// @ts-expect-error - TS2769 - No overload matches this call.

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener('openIssueCreateDialog', openIssueCreateDialogHandler);
		subscriptionHandle.current = () =>
			// @ts-expect-error - TS2769 - No overload matches this call.

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('openIssueCreateDialog', openIssueCreateDialogHandler);

		return () => {
			subscriptionHandle.current();
			subscriptionHandle.current = noop;
		};
	}, [openIssueCreateDialogHandler, subscriptionHandle]);

	return null;
};

export default NewIssueCreateTrigger;

export const useIssueCreateWithFieldPrefilling = () => {
	const [, { openIssueCreateModal }] = useTriggerIssueCreateModal();
	const getDefaultValues = usePrefilledDefaultValues();

	const openIssueCreateModalHandler = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		async (triggerPointKey: string, fields: Record<string, any>, callback: Function) => {
			EcosystemAPIPrefillingUFOExperience.start();
			let fieldList = fields;

			const {
				defaultValues: defaultValuesFromAPI,
				issueType,
				projectId,
				requestType,
			} = await getDefaultValues({ prefilledFieldsData: fieldList });

			fieldList = {
				...fieldList,
				pid: projectId,
				issuetype: issueType,
				requestype: requestType,
			};

			let payload: IssueTriggerPayload | null = null;

			if (!isEmpty(fieldList)) {
				payload = transformGICPayload({ prefilledFields: fieldList }, true);
			}

			if (payload) {
				payload.defaultValues = mergeDefaultValues(defaultValuesFromAPI, payload?.defaultValues);
			}

			const prefilledCustomFields = filterCustomFields({ prefilledFields: fieldList });

			EcosystemAPIPrefillingUFOExperience.success();

			openIssueCreateModal({
				payload,
				// @ts-expect-error string is of type trigger point key, not adding this type as they are not decided yet
				triggerPointKey,
				prefilledCustomFields,
				ecosystemCallback: { callbackFunction: callback },
			});
		},
		[openIssueCreateModal, getDefaultValues],
	);

	return openIssueCreateModalHandler;
};

export const useIssueCreateModalWithPrefillingForIssueCreateEmbed = () => {
	const getDefaultValues = usePrefilledDefaultValues();
	const getPrefilledIssueCreateProps = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		async (fields?: Record<string, any>, callback?: Function) => {
			EcosystemAPIPrefillingUFOExperience.start();
			let fieldList = fields;

			const {
				defaultValues: defaultValuesFromAPI,
				issueType,
				projectId,
				requestType,
			} = await getDefaultValues({ prefilledFieldsData: fieldList, callContextAPI: true });

			fieldList = {
				...fieldList,
				pid: projectId,
				issuetype: issueType,
				requestype: requestType,
			};

			let payload: IssueTriggerPayload | null = null;

			if (!isEmpty(fieldList)) {
				payload = transformGICPayload({ prefilledFields: fieldList }, true);
			}

			if (payload) {
				payload.defaultValues = mergeDefaultValues(defaultValuesFromAPI, payload?.defaultValues);
			}

			const prefilledCustomFields = filterCustomFields({ prefilledFields: fieldList });

			EcosystemAPIPrefillingUFOExperience.success();

			return {
				issueCreatePayload: payload,
				prefilledCustomFields,
				ecosystemCallback: callback ? { callbackFunction: callback } : undefined,
			};
		},
		[getDefaultValues],
	);
	return getPrefilledIssueCreateProps;
};
